import _toConsumableArray from "@babel/runtime/helpers/esm/toConsumableArray";
import _typeof from "@babel/runtime/helpers/esm/typeof";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import { useEffect, useState } from "react";
import { useMemoizedFn } from 'ahooks';
import noop from 'lodash/noop';
var getEmptyProps = function getEmptyProps() {
  return {};
};
export default function useSelection(dataSource, primaryKey, options) {
  // selectable list
  var _ref = options || {},
    _ref$keepSelection = _ref.keepSelection,
    keepSelection = _ref$keepSelection === void 0 ? false : _ref$keepSelection,
    propsSelectedRowKeys = _ref.selectedRowKeys,
    maxSelection = _ref.maxSelection,
    _ref$getProps = _ref.getProps,
    getProps = _ref$getProps === void 0 ? getEmptyProps : _ref$getProps,
    _ref$onChange = _ref.onChange,
    onChange = _ref$onChange === void 0 ? noop : _ref$onChange,
    _ref$onSelect = _ref.onSelect,
    onSelect = _ref$onSelect === void 0 ? noop : _ref$onSelect,
    _ref$onSelectAll = _ref.onSelectAll,
    onSelectAll = _ref$onSelectAll === void 0 ? noop : _ref$onSelectAll,
    childrenKey = _ref.childrenKey,
    maxChildSelection = _ref.maxChildSelection,
    propsChildPrimaryKey = _ref.childPrimaryKey,
    propsSelectedChildrenKeys = _ref.selectedChildrenKeys,
    _ref$onChildSelect = _ref.onChildSelect,
    onChildSelect = _ref$onChildSelect === void 0 ? noop : _ref$onChildSelect;
  var _ref2 = options || {},
    enabledChild = _ref2.enabledChild;

  // 数据不全不可选
  if (!dataSource || !options) {
    return {
      enabled: false,
      selectedRowKeys: [],
      noneSelected: true,
      allSelected: false,
      indeterminate: false,
      isDisabled: function isDisabled() {
        return false;
      },
      isSelected: function isSelected() {
        return false;
      },
      isIndeterminate: function isIndeterminate() {
        return false;
      },
      getProps: getEmptyProps,
      select: noop,
      selectAll: noop,
      getSelectedRecords: function getSelectedRecords() {
        return [];
      }
    };
  }

  // 当 enabledChild 没有显式声明 false，则根据 childrenKey 是否存在来确定
  if (enabledChild === undefined && childrenKey) {
    enabledChild = true;
  }
  var _useState = useState([]),
    _useState2 = _slicedToArray(_useState, 2),
    selectedRowKeys = _useState2[0],
    setSelectedRowKeys = _useState2[1];
  var isControlled = propsSelectedRowKeys !== undefined;
  var currentSelectedRowKeys = isControlled ? propsSelectedRowKeys : selectedRowKeys;
  // 子元素相关
  var _useState3 = useState([]),
    _useState4 = _slicedToArray(_useState3, 2),
    selectedChildrenKeys = _useState4[0],
    setSelectedChildrenKeys = _useState4[1];
  var childPrimaryKey = propsChildPrimaryKey || primaryKey;
  var currentSelectedChildrenKeys = isControlled ? propsSelectedChildrenKeys : selectedChildrenKeys;
  var selectedChildrenMap = {};
  useEffect(function () {
    if (!keepSelection) {
      setSelectedRowKeys([]);
      setSelectedChildrenKeys([]);
    }
  }, [(dataSource === null || dataSource === void 0 ? void 0 : dataSource.length) && dataSource]);

  // 获得父元素
  var getRecord = function getRecord(id) {
    return dataSource === null || dataSource === void 0 ? void 0 : dataSource.find(function (record) {
      return record[primaryKey] === id;
    });
  };

  // 通过子元素的 key 获得父元素
  var getRecordByChildKey = function getRecordByChildKey(id) {
    return dataSource === null || dataSource === void 0 ? void 0 : dataSource.find(function (record) {
      var children = record[childrenKey];
      return !!(children !== null && children !== void 0 && children.find(function (child) {
        return child[childPrimaryKey] === id;
      }));
    });
  };

  // 父元素是否选中
  var isSelected = useMemoizedFn(function (record) {
    var recordKey;
    if (_typeof(record) === 'object') {
      recordKey = record[primaryKey];
    } else {
      recordKey = record;
    }
    return currentSelectedRowKeys.includes(recordKey);
  });

  // 父元素是否部分选中
  var isIndeterminate = function isIndeterminate(record) {
    var recordKey;
    if (_typeof(record) === 'object') {
      recordKey = record[primaryKey];
    } else {
      recordKey = record;
    }
    // 检查是否有子元素
    if (childrenKey) {
      var _getRecord;
      // 有子元素，检查中间态
      var _children = (_getRecord = getRecord(recordKey)) === null || _getRecord === void 0 ? void 0 : _getRecord[childrenKey];
      if (!Array.isArray(_children)) {
        return false;
      }
      var childrenKeys = _children.map(function (child) {
        return child[childPrimaryKey];
      });
      var hasSelected = !!childrenKeys.find(function (key) {
        return currentSelectedChildrenKeys.includes(key);
      });
      var isSelectedAll = childrenKeys.every(function (key) {
        return currentSelectedChildrenKeys.includes(key);
      });
      return !isSelectedAll && hasSelected;
    } else {
      // 没有子元素，没有中间态
      return false;
    }
  };

  // 子元素是否选中
  var isChildSelected = useMemoizedFn(function (record) {
    var recordKey;
    if (_typeof(record) === 'object') {
      recordKey = record[childPrimaryKey];
    } else {
      recordKey = record;
    }
    return selectedChildrenKeys.includes(recordKey);
  });
  var noneSelected = currentSelectedRowKeys.length === 0;
  var allSelected = dataSource.every(isSelected) && !noneSelected;
  var indeterminate = !noneSelected && !allSelected;
  var mapSelectedRows = useMemoizedFn(function (keys, records) {
    return records.filter(function (record) {
      var recordKey = record[primaryKey];
      return keys.includes(recordKey);
    });
  });

  // 获得选中的记录
  var getSelectedRecords = useMemoizedFn(function () {
    var keys = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : currentSelectedRowKeys;
    return mapSelectedRows(keys, dataSource);
  });

  // 获得选中的子元素
  var getSelectedChildren = useMemoizedFn(function () {
    var keys = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : currentSelectedChildrenKeys;
    var selectedChildren = [];
    dataSource.forEach(function (record) {
      var children = record[childrenKey];
      selectedChildren = selectedChildren.concat(children === null || children === void 0 ? void 0 : children.filter(function (child) {
        return keys.includes(child[childPrimaryKey]);
      }));
    });
    return selectedChildren;
  });
  var handleChange = useMemoizedFn(function (keys, records, childrenKeys, children) {
    if (!isControlled) {
      setSelectedRowKeys(keys);
    }

    // 指定了子元素
    if (childrenKey && childrenKeys) {
      setSelectedChildrenKeys(childrenKeys);

      // 处理数据结构
      records.forEach(function (record) {
        var recordKey = record[primaryKey];
        if (!keys.includes(recordKey)) {
          // 未选中，不处理
          return;
        }
        var childItems = record[childrenKey];
        childItems === null || childItems === void 0 || childItems.forEach(function (child) {
          var childKey = child[childPrimaryKey];
          if (childrenKeys.includes(childKey)) {
            if (!selectedChildrenMap[recordKey]) {
              selectedChildrenMap[recordKey] = [];
            }
            selectedChildrenMap[recordKey].push(childKey);
          }
        });
      });
    }

    // 触发响应
    onChange(keys, records, childrenKeys, children, selectedChildrenMap);
  });
  var handleSelect = useMemoizedFn(function (selected, record) {
    var recordKey = record[primaryKey];
    var newKeys = _toConsumableArray(currentSelectedRowKeys);
    var newChildrenKeys = _toConsumableArray(currentSelectedChildrenKeys);
    if (selected) {
      // 已经存在
      if (newKeys.includes(recordKey)) {
        return;
      }
      // 达到数量上限
      if (maxSelection && newKeys.length >= maxSelection) {
        return;
      }
    }
    if (selected) {
      newKeys.push(recordKey);
    } else {
      var _index = newKeys.findIndex(function (key) {
        return key === recordKey;
      });
      newKeys.splice(_index, 1);
    }

    // 重新获得选中的
    var records = getSelectedRecords(newKeys);

    // 检查是否有子元素
    if (childrenKey) {
      var _children2 = record[childrenKey];
      _children2 === null || _children2 === void 0 || _children2.forEach(function (child) {
        var childKey = child[childPrimaryKey];
        if (selected) {
          if (!newChildrenKeys.includes(childKey)) {
            newChildrenKeys.push(childKey);
          }
        } else {
          var childIndex = newChildrenKeys.findIndex(function (key) {
            return key === childKey;
          });
          if (childIndex > -1) {
            newChildrenKeys.splice(childIndex, 1);
          }
        }
      });
    }

    // 触发响应
    onSelect(selected, record, records);
    handleChange(newKeys, records, newChildrenKeys);
  });
  var handleSelectChild = useMemoizedFn(function (selected, child, record) {
    var childKey = child[childPrimaryKey];
    var recordKey;
    if (_typeof(record) === 'object') {
      recordKey = record[primaryKey];
    } else if (record) {
      recordKey = record;
      record = getRecord(recordKey);
    }
    if (!record) {
      record = getRecordByChildKey(childKey);
    }
    if (!record) {
      return;
    }
    if (!recordKey) {
      recordKey = record[primaryKey];
    }
    var newKeys = _toConsumableArray(currentSelectedRowKeys);
    var newChildrenKeys = _toConsumableArray(currentSelectedChildrenKeys);
    if (selected) {
      // 已经存在
      if (newChildrenKeys.includes(childKey)) {
        return;
      }
      // 达到数量上限
      if (maxChildSelection && newChildrenKeys.length >= maxChildSelection) {
        return;
      }
    }
    if (selected) {
      // 选中，加入
      newChildrenKeys.push(childKey);
    } else {
      // 取消选中，取出
      var _index2 = newChildrenKeys.findIndex(function (key) {
        return key === childKey;
      });
      if (_index2 > -1) {
        newChildrenKeys.splice(_index2, 1);
      }
    }

    // 重新获取选中的子元素
    var children = getSelectedChildren(newChildrenKeys);
    var isRecordSelected = false;
    if (childrenKey) {
      var _record$childrenKey;
      var target = (_record$childrenKey = record[childrenKey]) === null || _record$childrenKey === void 0 ? void 0 : _record$childrenKey.find(function (child) {
        return newChildrenKeys.includes(child[childPrimaryKey]);
      });
      if (target) {
        isRecordSelected = true;
      }
    }
    if (isRecordSelected) {
      if (!newKeys.includes(recordKey)) {
        newKeys.push(recordKey);
      }
    } else {
      var _index3 = newKeys.findIndex(function (key) {
        return key === recordKey;
      });
      if (_index3 > -1) {
        newKeys.splice(_index3, 1);
      }
    }
    var selectedRows = mapSelectedRows(newKeys, dataSource);

    // 触发响应
    onChildSelect(selected, child, children);
    handleChange(newKeys, selectedRows, newChildrenKeys, children);
  });
  var isDisabled = useMemoizedFn(function (key) {
    if (key === undefined) {
      return false;
    }
    var index = dataSource.findIndex(function (record) {
      return record[primaryKey] === key;
    });
    var _getProps = getProps(dataSource[index], index),
      disabled = _getProps.disabled;
    return disabled || maxSelection !== undefined && currentSelectedRowKeys.length >= maxSelection && !currentSelectedRowKeys.includes(key);
  });
  var handleSelectAll = useMemoizedFn(function (selected) {
    var selectedKeys = [];
    var selectedRows = [];
    var selectedSubKeys = [];
    if (selected) {
      var selectedSet = new Set(currentSelectedRowKeys);

      // 先将所有可以被选中的记录取出
      var selectedRecords = _toConsumableArray(dataSource).filter(function (record) {
        return !isDisabled(record[primaryKey]);
      });

      // 检查是否有限制
      if (maxSelection && selected) {
        // 只取指定数量的记录
        selectedRecords = selectedRecords.slice(0, maxSelection);
        if (selectedSet.size >= maxSelection) {
          // 已经全选，进行反选
          selected = false;
        }
      }
      if (selected) {
        selectedRecords.forEach(function (item) {
          return selectedSet.add(item[primaryKey]);
        });
      } else {
        // 反选，删除所有的 key
        dataSource.forEach(function (item) {
          return selectedSet.delete(item[primaryKey]);
        });
      }

      // 所有选中的父元素
      selectedKeys = Array.from(selectedSet);
      selectedRows = mapSelectedRows(selectedKeys, dataSource);

      // 如果有子元素则需要一起处理
      if (childrenKey) {
        selectedRows.forEach(function (item) {
          var children = item[childrenKey];
          children === null || children === void 0 || children.forEach(function (child) {
            if (child) {
              selectedSubKeys.push(child[childPrimaryKey]);
            }
          });
        });
      }
    }

    // 触发响应
    onSelectAll(selected, selectedRows);
    handleChange(selectedKeys, selectedRows, selectedSubKeys);
  });
  return {
    enabled: true,
    selectedRowKeys: currentSelectedRowKeys,
    isDisabled: isDisabled,
    isSelected: isSelected,
    isIndeterminate: isIndeterminate,
    noneSelected: noneSelected,
    allSelected: allSelected,
    indeterminate: indeterminate,
    getProps: getProps,
    select: handleSelect,
    selectAll: handleSelectAll,
    getSelectedRecords: getSelectedRecords,
    // 子元素相关
    enabledChild: enabledChild,
    childrenKey: childrenKey,
    selectedChildrenKeys: selectedChildrenKeys,
    selectedChildrenMap: selectedChildrenMap,
    isChildSelected: isChildSelected,
    selectChild: handleSelectChild,
    getSelectedChildren: getSelectedChildren
  };
}